import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

import { Field } from '@/components/ui/controls'

import { toast as message } from 'sonner'
import { ConfirmAction } from '../products/PageHelpers'
import { Plus, Copy, RefreshCw, Eye, EyeOff } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useMerchantStore } from '../../stores/merchantStore'
import {
  getMerchant,
  updateWebhook,
  regenerateWebhookSecret,
} from '../../services/merchant'

export default function Webhooks() {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const { merchant, setMerchant } = useMerchantStore()
  const [webhookUrl, setWebhookUrl] = useState('')

  const [showSecret, setShowSecret] = useState(false)
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState('')

  useEffect(() => {
    setLoading(true)
    getMerchant()
      .then((res) => {
        setMerchant(res.data)
        setWebhookUrl(res.data.webhookUrl || '')
      })
      .finally(() => setLoading(false))
  }, [setMerchant])

  const hasSecret = merchant?.hasWebhookSecret || webhookSecret !== null

  const handleSubmit = async (values: { webhookUrl: string }) => {
    setSaving(true)
    try {
      const res = await updateWebhook(values.webhookUrl, totpCode || undefined)
      if (res.data.webhookSecret) {
        setWebhookSecret(res.data.webhookSecret)
        setShowSecret(true)
        message.success('Webhook URL 已更新，Secret 已生成')
      } else {
        message.success('Webhook URL 已更新')
      }
      getMerchant().then((r) => setMerchant(r.data))
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '更新失敗')
    } finally {
      setSaving(false)
    }
  }

  const handleCopySecret = () => {
    if (webhookSecret) {
      navigator.clipboard.writeText(webhookSecret)
      message.success('已複製到剪貼板')
    }
  }

  const handleGenerateSecret = async () => {
    setRegenerating(true)
    try {
      const res = await regenerateWebhookSecret(totpCode || undefined)
      setWebhookSecret(res.data.secret)
      setShowSecret(true)
      getMerchant().then((r) => setMerchant(r.data))
      message.success(
        hasSecret ? 'Webhook Secret 已重新生成' : 'Webhook Secret 已生成',
      )
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '操作失敗')
    } finally {
      setRegenerating(false)
    }
  }

  const secretButton = (
    <Button
      onClick={hasSecret ? undefined : handleGenerateSecret}
      variant={'outline'}
      disabled={Boolean(regenerating)}
      type="button"
    >
      {hasSecret ? <RefreshCw /> : <Plus />}
      {hasSecret ? '重新生成' : '生成'}
    </Button>
  )

  return (
    <div>
      <h2
        style={{ marginBottom: 24 }}
        className="mb-4 text-xl font-semibold tracking-tight"
      >
        Webhooks
      </h2>

      <Card style={{ marginBottom: 24 }}>
        <div className="space-y-4 px-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <h3 className="font-semibold">{'Webhook 設定'}</h3>
          </div>
          {loading ? (
            <div role="status">載入中…</div>
          ) : (
            <>
              <p>
                當訂單、退款或訂閱生命週期發生已確認的業務事件時，我們會向您設定的
                Webhook URL 發送 HTTP POST 通知。
              </p>
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  void handleSubmit({ webhookUrl })
                }}
                className="max-w-lg space-y-5"
              >
                <Field label="Webhook URL" htmlFor="webhook-url" required>
                  <Input
                    id="webhook-url"
                    required
                    type="url"
                    pattern="https://.*"
                    value={webhookUrl}
                    onChange={(event) => setWebhookUrl(event.target.value)}
                    placeholder="https://your-domain.com/webhooks/kyren"
                  />
                </Field>
                {merchant?.twoFactorEnabled && (
                  <Field label="2FA 驗證碼" htmlFor="webhook-totp">
                    <Input
                      id="webhook-totp"
                      value={totpCode}
                      onChange={(event) => setTotpCode(event.target.value)}
                      maxLength={6}
                    />
                  </Field>
                )}
                <Button type="submit" disabled={saving}>
                  儲存
                </Button>
              </form>
            </>
          )}
        </div>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <div className="space-y-4 px-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <h3 className="font-semibold">{'Webhook 簽名驗證'}</h3>
          </div>
          <p>每個 Webhook 請求都包含簽名頭，用於驗證請求來自啟潤支付。</p>
          <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">{'簽名頭'}</dt>
              <dd className="break-words text-sm">X-Kyren-Signature</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'時間戳頭'}
              </dt>
              <dd className="break-words text-sm">X-Kyren-Timestamp</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'Webhook Secret'}
              </dt>
              <dd className="break-words text-sm">
                {merchant?.webhookUrl ? (
                  <div
                    style={{ width: '100%' }}
                    className={'flex flex-wrap items-center gap-2'}
                  >
                    <Input
                      value={
                        webhookSecret
                          ? showSecret
                            ? webhookSecret
                            : '••••••••••••••••••••••••'
                          : hasSecret
                            ? 'whsec_••••••••••••••••'
                            : ''
                      }
                      readOnly={true}
                      placeholder={
                        hasSecret ? undefined : '尚未生成 Webhook Secret'
                      }
                      style={{ fontFamily: 'monospace', flex: 1 }}
                    />
                    {webhookSecret && (
                      <Button
                        onClick={() => setShowSecret(!showSecret)}
                        variant={'outline'}
                        type="button"
                        aria-label={'顯示或隱藏密鑰'}
                      >
                        {showSecret ? <EyeOff /> : <Eye />}
                      </Button>
                    )}
                    {webhookSecret && (
                      <Button
                        onClick={handleCopySecret}
                        variant={'outline'}
                        type="button"
                        aria-label={'複製'}
                      >
                        {<Copy />}
                      </Button>
                    )}
                    {hasSecret ? (
                      <ConfirmAction
                        title={'確定要重新生成 Webhook Secret 嗎？'}
                        description={
                          '舊的 Secret 將立即失效，您需要更新服務端設定。'
                        }
                        onConfirm={handleGenerateSecret}
                      >
                        {secretButton}
                      </ConfirmAction>
                    ) : (
                      secretButton
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">
                    設定 Webhook URL 後可生成
                  </span>
                )}
              </dd>
            </div>
          </dl>
          <p style={{ marginTop: 16 }}>
            簽名輸入為 <code>{'${timestamp}.${rawBody}'}</code>，簽名格式為{' '}
            <code>sha256=&lt;hex-hmac-sha256&gt;</code>。請使用收到的 raw body
            原始內容驗簽，
            <code>X-Kyren-Timestamp</code>為毫秒時間戳。
          </p>
        </div>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <div className="space-y-4 px-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <h3 className="font-semibold">{'支援的事件'}</h3>
          </div>
          <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'subscription.created'}
              </dt>
              <dd className="break-words text-sm">首期扣款成功並啟用訂閱</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'subscription.cancelled'}
              </dt>
              <dd className="break-words text-sm">訂閱實際進入已取消終態</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'subscription.terminated'}
              </dt>
              <dd className="break-words text-sm">訂閱不可恢復地終止</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'invoice.paid'}
              </dt>
              <dd className="break-words text-sm">某一期訂閱賬單扣款成功</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'invoice.payment_failed'}
              </dt>
              <dd className="break-words text-sm">
                某一期訂閱賬單最終扣款失敗
              </dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'order.paid'}
              </dt>
              <dd className="break-words text-sm">訂單支付成功</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'order.refunded'}
              </dt>
              <dd className="break-words text-sm">訂單退款成功</dd>
            </div>
            <div className="min-w-0 bg-white p-3">
              <dt className="mb-1 text-xs text-muted-foreground">
                {'order.closed'}
              </dt>
              <dd className="break-words text-sm">未支付訂單已關閉</dd>
            </div>
          </dl>
          <p style={{ marginTop: 16 }}>
            每個 payload 都包含 <code>id</code>、<code>type</code>、
            <code>created_at</code>和 <code>data</code>。任意 2xx 響應視為 ACK；
            單次請求超時為 30 秒，最多 7 次總投遞。投遞語義為 at-least-once，
            接收方必須按 payload id 去重。
          </p>
        </div>
      </Card>
    </div>
  )
}
